import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { FormTemplate } from '../types/formBuilder';
import { TemplateService } from '../services/templateService';
import { OptimizedFormSubmissionService as FormSubmissionService } from '../services/optimizedFormSubmissionService';
import FormRenderer from '../components/forms/FormRenderer';
import LoadingScreen from '../components/common/LoadingScreen';
import CommonPageHeader from '../components/common/CommonPageHeader';
import { 
  DocumentTextIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

const FormFiller: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();
  
  const [template, setTemplate] = useState<FormTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (templateId) {
      loadTemplate();
    }
  }, [templateId]);

  const loadTemplate = async () => {
    if (!templateId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const loadedTemplate = await TemplateService.getFormTemplate(templateId);
      
      if (!loadedTemplate) {
        setError(t('formFiller.templateNotFound') || 'Form template not found');
        return;
      }

      // Check permissions
      if (!canUserFillForm(loadedTemplate)) {
        setError(t('formFiller.noPermission') || 'You do not have permission to fill this form');
        return;
      }

      setTemplate(loadedTemplate);
    } catch (error) {
      console.error('Error loading template:', error);
      setError(t('formFiller.loadError') || 'Error loading form template');
    } finally {
      setLoading(false);
    }
  };

  const canUserFillForm = (template: FormTemplate): boolean => {
    if (!user) return false;
    
    // Check if user has permission to submit this form
    const canSubmit = template.permissions.canSubmit;
    
    // If no specific permissions set, allow all authenticated users
    if (!canSubmit || canSubmit.length === 0) {
      return true;
    }
    
    // Check if user's role or ID is in the canSubmit list
    return canSubmit.includes(user.role || '') || 
           canSubmit.includes(user.uid) ||
           canSubmit.includes('all') ||
           canSubmit.includes('authenticated');
  };

  const handleFormSubmit = async (data: any) => {
    if (!template || !user) return;

    try {
      // Create form submission
      const submittedByName = user.displayName || 
                             user.email?.split('@')[0] || 
                             `User ${user.uid.slice(-6)}` || 
                             'Unknown User';
                             
      await FormSubmissionService.createSubmission({
        templateId: template.id,
        templateName: template.name,
        submittedBy: user.uid,
        submittedByName,
        organizationId: user.organizationId || '',
        data,
        status: template.settings.allowDrafts ? 'draft' : 'submitted',
        metadata: {
          userAgent: navigator.userAgent,
          source: 'form_filler'
        }
      });

      toast.success(
        template.settings.allowDrafts 
          ? (t('formFiller.draftSaved') || 'Draft saved successfully')
          : (t('formFiller.submitted') || 'Form submitted successfully')
      );

      // Navigate back to form builder page
      navigate('/form-builder');
    } catch (error) {
      console.error('Error submitting form:', error);
      throw error; // Re-throw to let FormRenderer handle the error display
    }
  };

  const handleCancel = () => {
    // Navigate back to form builder page instead of generic back
    navigate('/form-builder');
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <CommonPageHeader
          title={t('formFiller.title') || 'Fill Form'}
          subtitle={t('formFiller.subtitle') || 'Complete the form below'}
          icon={DocumentTextIcon}
        />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
            <div className="text-red-500 mb-4">
              <DocumentTextIcon className="h-12 w-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              {t('formFiller.errorTitle') || 'Unable to Load Form'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {error}
            </p>
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md hover:bg-gray-50 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              {t('common.back') || 'Go Back'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!template) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <CommonPageHeader
        title={template.settings.title || template.name}
        subtitle={template.settings.description || (t('formFiller.subtitle') || 'Complete the form below')}
        icon={DocumentTextIcon}
      >
        <button
          onClick={handleCancel}
          className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          {t('common.back') || 'Back'}
        </button>
      </CommonPageHeader>
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <FormRenderer
          template={template}
          onSubmit={handleFormSubmit}
          onCancel={handleCancel}
        />
      </div>
    </div>
  );
};

export default FormFiller;
